import { isNil } from "lodash";
import { CatalogItemRestriction, CatalogItemRestrictionScope, ProductCatalogItem } from "types";

export const hasOverridableRestriction = (restrictions: CatalogItemRestriction[] = []) => {
    return restrictions.some((restriction) => restriction.allowOverride);
};

export const SOLD_OUT_MESSAGE = "Sold Out";

export const getSoldOutLimitError = (limitError: string | undefined, isSoldOut: boolean) => {
    return limitError ?? (isSoldOut ? SOLD_OUT_MESSAGE : undefined);
};

interface AddonClientMaxParams {
    guestCount: number;
    isOverriding?: boolean;
    /**
     * The most this item may hold under the per event limit, from the API's last rejection. The client cannot see
     * other bookings.
     */
    catalogItemOpen?: number;
    quantitySelectedByOthers?: number;
    catalogItem: Pick<ProductCatalogItem, "restrictions">;
}

/**
 * The limits the client can compute. The event limit is only an upper bound until the API reports what the event has
 * left, and the API stays the authority.
 */
export const getAddonClientMax = ({
    catalogItem,
    guestCount,
    catalogItemOpen,
    isOverriding = false,
    quantitySelectedByOthers = 0,
}: AddonClientMaxParams) => {
    const restrictions = (catalogItem.restrictions ?? []).filter((restriction) => {
        return !(isOverriding && restriction.allowOverride);
    });

    const caps: number[] = [];
    for (const restriction of restrictions) {
        // Other attendees' quantities count against the booking and event limits.
        const isSharedLimit =
            restriction.scope === CatalogItemRestrictionScope.PURCHASE_ITEM ||
            restriction.scope === CatalogItemRestrictionScope.EVENT;
        if (isSharedLimit) {
            caps.push(Math.max(restriction.value - quantitySelectedByOthers, 0));
        }
        if (restriction.scope === CatalogItemRestrictionScope.EVENT && !isNil(catalogItemOpen)) {
            caps.push(Math.max(catalogItemOpen - quantitySelectedByOthers, 0));
        }
        // A zero guest count would compute a cap of 0 and lock the spinner before guests are picked.
        if (restriction.scope === CatalogItemRestrictionScope.PRIMARY_UNIT && guestCount > 0) {
            caps.push(restriction.value * guestCount);
        }
    }

    return caps.length > 0 ? Math.min(...caps) : undefined;
};
